import {
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Box,
  OutlinedInput,
  DialogContentText,
  FormLabel,
} from "@material-ui/core";
import { closeModal } from "actions/modalActions";
import { useModalStyles } from "../styles";
import { useDispatch } from "react-redux";
import { Dispatch, FC, useEffect, useState } from "react";
import { OnChangeEvent } from "types/general";
import { CustomButton } from "componentsNew/buttons/CustomButton";
import { giveAccessToOldShifts } from "actions/authAction";
import api from "api";
import { customToast } from "hooks/toast";
import { useNav } from "hooks/useNav";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import moment from "moment";
import { Station } from "types/DataTypes";
import {
  CreateReportAction,
  notifyReportDriversAction,
  removeReportAction,
  updateReportAction,
  updateReportCommentAction,
  updateReportRecordStateAction,
} from "actions/shiftTrackerActions";
import { ReportRecord, Van } from "types/ShiftTracker";
import { FleetData } from "types/DaAndFleet";
import { fleetReasonConstantList, MENU_PROPS } from "constants/constants";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import CustomIconButton from "componentsNew/buttons/CustomIconButton";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

interface EnableSecondDriverModalProps {}
interface DialogProps {}
interface DeleteShiftModalContentProps {
  reportId: number;
  handleClose: () => void;
}
interface ReportCommentModalContentProps {
  reportId: number;
  commentText: string | null;
  handleClose: () => void;
}

export const GiveAccessToOldShiftsModalContent: FC<DialogProps> = () => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<number | null>(null);
  const [data, setData] = useState([]);

  const onClose = () => dispatch(closeModal());

  const handleGetUsersList = () => {
    setLoading(true);
    api
      .getUsersList()
      .then((res) => {
        if (res.data.results) {
          setData(res.data.results);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  };

  const handleSubmit = () => {
    if (user) {
      api
        .setUserAccess(user)
        .then((res) => {
          dispatch(closeModal());
          dispatch(giveAccessToOldShifts());
          customToast("success", `${res?.data?.ok}`);
        })
        .catch((err) => {
          console.error(err);
          customToast("error", "Server error");
        });
    }
  };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <FormControl>
          <Typography className={classes.textLabel}>Select user</Typography>
          <Select
            className={!user ? classes.noSelectionOption : ""}
            variant="outlined"
            value={user ? user : 0}
            onOpen={handleGetUsersList}
            onChange={(e: OnChangeEvent) => setUser(e.target.value as number)}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 208,
                  width: 240,
                },
              },
              variant: "menu",
              getContentAnchorEl: null,
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              transformOrigin: { vertical: "top", horizontal: "center" },
            }}
          >
            {loading && <CircularProgress />}
            <MenuItem key={0} value={0} disabled>
              No selection
            </MenuItem>
            {data ? (
              data.map((el: { id: number; first_name: string; last_name: string }) => (
                <MenuItem key={el.id} value={el.id}>
                  {el.first_name} {el.last_name}
                </MenuItem>
              ))
            ) : (
              <MenuItem key={0} value={0}></MenuItem>
            )}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton buttonText={"Save"} onClick={handleSubmit} variant="contained" />
      </DialogActions>
    </>
  );
};

export const CreateShiftModalContent: FC<DialogProps> = () => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const { viewReport } = useNav();
  const { userData } = useTypeSelectors((state) => state.auth);
  const { reportListFilters } = useTypeSelectors((state) => state.dasBoard);
  const { isOpen } = useTypeSelectors((state) => state.modalState);
  const minReportDate = moment().format("yyyy-MM-DD");
  const defaultReportDate = moment().add(1, "days").format("yyyy-MM-DD");
  const defaultStation = reportListFilters.station
    ? reportListFilters.station
    : userData.station_spots.length
      ? userData.station_spots[0].station.id
      : null;
  const conditionForCreateOldShifts = userData?.can_create_old_shifts ? null : minReportDate;

  const [station, setStation] = useState<number | null>(defaultStation || null);
  const [date, setDate] = useState<string>(defaultReportDate);
  const [rows, setRows] = useState<number>(0);
  const [hasSecondDriver, setHasSecondDriver] = useState<boolean>(false);
  const [shiftsByStation, setShiftsByStation] = useState<number[]>([]);

  const onClose = () => dispatch(closeModal());

  const handleSubmit = () => {
    if (station && date) {
      onClose();
      dispatch(CreateReportAction({ station, date, rows, hasSecondDriver }, viewReport));
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setStation(null);
      setDate(defaultReportDate);
    }
  }, [isOpen, defaultReportDate]);

  useEffect(() => {
    if (isOpen && (station || date)) {
      api
        .checkIsShiftDuplicate({ date })
        .then((res) => {
          setShiftsByStation(
            res.data.map((station: { station__id: number }) => station.station__id),
          );
        })
        .catch((err) => setShiftsByStation([]));
    }
  }, [station, date, isOpen]);

  const conditionStation = (station: number | null) => {
    return station ? shiftsByStation.includes(Number(station)) : false;
  };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <FormControl>
          <Typography className={classes.textLabel}>Station</Typography>
          <Select
            variant="outlined"
            name="station"
            value={station || defaultStation}
            onChange={(e: OnChangeEvent) => setStation(e.target.value as number)}
            className={!station ? classes.noSelectionOption : ""}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 208,
                  width: 240,
                },
              },
              variant: "menu",
              getContentAnchorEl: null,
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              transformOrigin: { vertical: "top", horizontal: "center" },
            }}
          >
            {userData.station_spots ? (
              userData?.station_spots?.map((el: { station: Station }) => (
                <MenuItem
                  key={el.station.id}
                  value={el.station.id}
                  disabled={conditionStation(el.station.id)}
                >
                  {el.station.title}
                </MenuItem>
              ))
            ) : (
              <MenuItem key={0} value={0}></MenuItem>
            )}
          </Select>
        </FormControl>
        <FormControl style={{ marginTop: 20 }}>
          <Typography className={classes.textLabel}>Shift Date</Typography>
          <TextField
            name="report_date"
            variant="outlined"
            type="date"
            value={date}
            onChange={(e: OnChangeEvent) => setDate(e.target.value)}
            InputProps={{
              inputProps: {
                min: conditionForCreateOldShifts,
                "data-testid": "reportCreateDateInput",
              },
            }}
          />
        </FormControl>
        <FormControl style={{ marginTop: 20, marginBottom: 20 }}>
          <Typography className={classes.textLabel}>Number of Rows</Typography>
          <TextField
            name="rows"
            type="number"
            variant="outlined"
            value={rows}
            onChange={(e: OnChangeEvent) => setRows(Number(e.target.value))}
            InputProps={{
              inputProps: {
                min: 0,
              },
            }}
          />
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={hasSecondDriver}
              onChange={() => setHasSecondDriver(!hasSecondDriver)}
              name="has_second_driver"
            />
          }
          label="Show Helper"
        />
        {conditionStation(station) && (
          <Typography className={classes.errorMsg}>
            Shift, for selected date and station, already added*
          </Typography>
        )}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton
          buttonText={"Save"}
          onClick={handleSubmit}
          variant="contained"
          disabled={conditionStation(station)}
        />
      </DialogActions>
    </>
  );
};

export const ReportCommentModalContent: FC<ReportCommentModalContentProps> = ({
  reportId,
  handleClose,
  commentText,
}) => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const [comment, setComment] = useState<string>(commentText || "");
  const onClose = () => dispatch(closeModal());

  const handleSubmit = () => {
    onClose();
    handleClose();
    dispatch(updateReportCommentAction(reportId, { report_comment: comment }));
  };

  return (
    <>
      <DialogContent>
        <FormControl style={{ marginBottom: 20, width: 400 }}>
          <TextField
            name="edit_comment"
            variant="outlined"
            multiline
            minRows={6}
            maxRows={8}
            defaultValue={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </FormControl>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton buttonText={"Comment"} onClick={handleSubmit} variant="contained" />
      </DialogActions>
    </>
  );
};

export const DeleteShiftModalContent: FC<DeleteShiftModalContentProps> = ({
  reportId,
  handleClose,
}) => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const onClose = () => dispatch(closeModal());

  const handleSubmit = () => {
    onClose();
    handleClose();
    dispatch(removeReportAction(reportId));
  };

  return (
    <>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton
          buttonText={"Delete"}
          theme={"red"}
          onClick={handleSubmit}
          variant="contained"
        />
      </DialogActions>
    </>
  );
};

export const EditWavesModalContent: FC<{}> = () => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const { currentReportId, currentWaveStartTimeList } = useTypeSelectors((state) => state.dasBoard);
  const [waveList, setWaveList] = useState(currentWaveStartTimeList);

  useEffect(() => {
    setWaveList(currentWaveStartTimeList);
  }, [currentWaveStartTimeList]);

  const onSubmit = () => {
    dispatch(updateReportAction(currentReportId, { wave_start_time: waveList }));
  };

  const onClose = () => dispatch(closeModal());
  const handleSubmit = () => {
    onClose();
    if (onSubmit) onSubmit();
  };

  return (
    <>
      <DialogContent>
        <Box style={{ display: "flex", justifyContent: "center", gap: 24 }}>
          <Box style={{ flexGrow: 1 }}>
            {waveList.slice(0, 5).map((wave) => (
              <EditWavesRow
                wave_name={wave.name}
                start_time={wave.start_time}
                setAction={setWaveList}
              />
            ))}
          </Box>
          <Box style={{ flexGrow: 1 }}>
            {waveList.slice(5, 10).map((wave) => (
              <EditWavesRow
                wave_name={wave.name}
                start_time={wave.start_time}
                setAction={setWaveList}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton buttonText={"Save"} onClick={handleSubmit} variant="contained" />
      </DialogActions>
    </>
  );
};

const EditWavesRow: FC<{
  wave_name: string;
  start_time: string;
  setAction: Dispatch<
    React.SetStateAction<
      {
        name: string;
        value: number;
        start_time: string;
      }[]
    >
  >;
}> = ({ wave_name, start_time, setAction }) => {
  const [value, setValue] = useState(start_time);
  const onChangeValue = (time: string) => {
    setValue(time);
    setAction((prev) =>
      prev.map((wave) => (wave.name === wave_name ? { ...wave, start_time: time } : wave)),
    );
  };
  return (
    <Box style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 24 }}>
      <Typography style={{ color: "#5F6164" }}>{wave_name}</Typography>
      <FormControl>
        <TextField
          name="shift_start_time"
          type="time"
          variant="outlined"
          value={value}
          onChange={(e: OnChangeEvent) => onChangeValue(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </FormControl>
    </Box>
  );
};

export const EditShiftCommentModalContent: FC<{
  onSubmit: any;
  commentText: string;
}> = ({ onSubmit, commentText }) => {
  const dispatch = useDispatch();
  const classes = useModalStyles();
  const [value, setValue] = useState(commentText);

  useEffect(() => {
    setValue(commentText);
  }, [commentText]);

  const onClose = () => dispatch(closeModal());
  const handleSubmit = () => {
    onClose();
    onSubmit({ actual_DA: value });
  };

  return (
    <>
      <DialogContent style={{ marginBottom: 16 }}>
        <OutlinedInput
          fullWidth
          multiline
          defaultValue={value}
          onChange={(e) => setValue(e.target.value)}
          minRows={6}
          maxRows={6}
        />
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton
          buttonText={"Save"}
          onClick={handleSubmit}
          variant="contained"
          disabled={value === commentText}
        />
      </DialogActions>
    </>
  );
};

export const EditShiftVanModalContent: FC<{
  shiftData: ReportRecord;
  shiftDate: string;
  shiftStation: number;
  disabledCondition: boolean;
}> = ({ shiftData, shiftDate, shiftStation, disabledCondition }) => {
  const dispatch = useDispatch();
  const classes = useModalStyles();
  const [vans, setVans] = useState<FleetData[]>([]);
  const [value, setValue] = useState(shiftData);
  const [van, setVan] = useState(value.van?.id);
  const [reason, setReason] = useState(shiftData.assignments[0]?.reason || 0);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    api
      .getAvailableVans({
        date: shiftDate,
        station: shiftStation,
        driver: value.driver?.id,
        available: true,
      })
      .then((res) => {
        if (res?.data?.results) {
          setVans(res.data.results);
          setLoading(false);
        }
      })
      .catch(() => {
        handleClearDrivers();
      });
  }, [shiftDate, shiftStation, value.driver?.id]);

  useEffect(() => {
    setValue(shiftData);
  }, [shiftData]);

  useEffect(() => {
    setValue(shiftData);
  }, [shiftData]);

  const handleClearDrivers = () => {
    setVans([]);
    setLoading(false);
  };

  const onClose = () => dispatch(closeModal());
  const handleSubmit = async () => {
    onClose();
    await api
      .setVanAssignment(value.assignments[0].id, {
        date: shiftDate,
        station: shiftStation,
        van: van,
        reason: reason,
      })
      .then((res) => {
        if (van) {
          dispatch(
            updateReportRecordStateAction(shiftData.id, {
              ...shiftData,
              assignments: [res?.data["assignment"]],
              van: { ...res?.data["van"] },
            }),
          );
        } else {
          dispatch(
            updateReportRecordStateAction(shiftData.id, {
              ...shiftData,
              assignments: [{ ...shiftData.assignments[0], van: null }],
              van: null,
            }),
          );
        }
      })
      .catch(() => {
        dispatch(updateReportRecordStateAction(shiftData.id, { ...shiftData, van: null }));
      });
  };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <Box style={{ display: "flex", gap: 32, alignItems: "center", marginBottom: 16 }}>
          <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <PersonOutlinedIcon style={{ color: "#5F6164" }} />
            <Typography>
              {value.driver?.first_name} {value.driver?.last_name}
            </Typography>
          </Box>
          <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <LocalShippingOutlinedIcon style={{ color: "#5F6164" }} />
            <Typography>{!value.van ? "---" : value.van?.number}</Typography>
          </Box>
        </Box>
        <FormControl style={{ marginBottom: 20 }}>
          <Typography className={classes.textLabel}>Select Fleet</Typography>
          <Select
            fullWidth
            MenuProps={MENU_PROPS}
            variant="outlined"
            disabled={disabledCondition}
            value={van || 0}
            onChange={(e: OnChangeEvent) => setVan(Number(e.target.value))}
          >
            <MenuItem key={0} value={0}>
              Unassigned van
            </MenuItem>
            {value.van ? returnFleetLabel(value.van) : null}
            {loading ? (
              <MenuItem key="loading" disabled>
                <CircularProgress />
              </MenuItem>
            ) : (
              vans.map((el: FleetData) => (
                <MenuItem key={el.id} value={el.id}>
                  {el.number}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        <FormControl>
          <Typography className={classes.textLabel}>Reason</Typography>
          <Select
            fullWidth
            MenuProps={MENU_PROPS}
            variant="outlined"
            disabled={disabledCondition}
            value={reason || 0}
            onChange={(e: OnChangeEvent) => setReason(Number(e.target.value))}
          >
            {fleetReasonConstantList.map((el) => (
              <MenuItem key={el.id} value={el.id}>
                {el.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton
          buttonText={"Confirm"}
          onClick={handleSubmit}
          disabled={
            !van ||
            !reason ||
            (reason === shiftData.assignments[0]?.reason && van === shiftData.van?.id)
          }
          variant="contained"
        />
      </DialogActions>
    </>
  );
};

export const EnableSecondDriverModal: FC<EnableSecondDriverModalProps> = () => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const { currentReportId, hasSecondDriver } = useTypeSelectors((state) => state.dasBoard);

  const onClose = () => dispatch(closeModal());

  const handleSubmit = () => {
    onClose();
    dispatch(updateReportAction(currentReportId, { has_second_driver: !hasSecondDriver }));
  };

  return (
    <>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton buttonText={"Confirm"} onClick={handleSubmit} variant="contained" />
      </DialogActions>
    </>
  );
};

export const SendDriversMessageModal: FC<{}> = () => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const { currentReportId, currentReport } = useTypeSelectors((state) => state.dasBoard);

  const onClose = () => dispatch(closeModal());
  const handleSendShiftAttendance = () => {
    onClose();
    dispatch(notifyReportDriversAction(currentReportId, "shift_attendance"));
  };
  const handleSendShiftStatus = () => {
    onClose();
    dispatch(notifyReportDriversAction(currentReportId, "shift_status"));
  };

  const unpublishedMessages =
    currentReport.filter(
      (record) => 0 === record.da_status && !record.is_status_published && record.driver,
    ).length +
    currentReport.filter(
      (record) =>
        0 === record.second_da_status && !record.is_second_status_published && record.second_driver,
    ).length;

  const unpublishedStatuses =
    currentReport.filter(
      (record) => 0 !== record.da_status && !record.is_status_published && record.driver,
    ).length +
    currentReport.filter(
      (record) =>
        0 !== record.second_da_status && !record.is_second_status_published && record.second_driver,
    ).length;

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>
          Are you sure you want to notify the drivers from this list that changes was confirmed?
        </DialogContentText>
        <DialogContentText>
          They will receive an SMS with a notification. It may take some time to deliver them.
        </DialogContentText>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton
          buttonText={`Attendance Confirmation (${unpublishedMessages})`}
          onClick={handleSendShiftAttendance}
          variant="contained"
        />
        <CustomButton
          buttonText={`Notify DA about Shift status (${unpublishedStatuses})`}
          onClick={handleSendShiftStatus}
          variant="contained"
        />
      </DialogActions>
    </>
  );
};

export const UploadExeShiftDataModal: FC<{}> = () => {
  const classes = useModalStyles();
  const dispatch = useDispatch();
  const [file, setFile] = useState<File | null>(null);
  const { currentReportDate } = useTypeSelectors((state) => state.dasBoard);

  const onClose = () => dispatch(closeModal());

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  const handleSubmit = () => {
    if (file != null) {
      handleClose();
      const request: FormData = new FormData();
      request.append("xlsx_file", file);
      request.append("shift_date", currentReportDate);
      api
        .uploadXlsxShiftData(request)
        .then((res) => {
          if (res.data) {
            customToast("success", "Xlsx file was uploaded!");
          }
        })
        .catch(() => customToast("error", "Xlsx file was not uploaded! Please try again."));
    }
  };

  const onClear = () => {
    setFile(null);
  };

  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <FormLabel htmlFor="btn-upload">
          <input
            id="btn-upload"
            name="btn-upload"
            style={{ display: "none" }}
            type="file"
            accept=".xlsx"
            onChange={(e) => {
              if (e?.target?.files?.[0]) {
                setFile(e?.target?.files?.[0]);
              }
            }}
          />
          <CustomButton
            buttonText={"Choose File"}
            theme="blue"
            onClick={() => null}
            variant="outlined"
            component={"span"}
          />
        </FormLabel>
        {file ? <FileCard file={file} onClear={onClear} /> : null}
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <CustomButton buttonText={"Cancel"} onClick={onClose} variant="text" />
        <CustomButton
          buttonText={"Upload"}
          onClick={handleSubmit}
          variant="contained"
          disabled={file === null}
        />
      </DialogActions>
    </>
  );
};

const FileCard = ({ file, onClear }: { file: File; onClear: () => void }) => {
  return (
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        marginTop: 24,
        justifyContent: "space-between",
      }}
    >
      <Box style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <InsertDriveFileOutlinedIcon fontSize={"large"} />
        <Box>
          <Typography>
            {file.name.length > 32 ? `${file.name.slice(0, 32)}...` : file.name}
          </Typography>
        </Box>
      </Box>
      <CustomIconButton
        icon={<CloseIcon />}
        iconColor={"darkGray"}
        theme={"blank"}
        action={onClear}
      />
    </Box>
  );
};

const returnFleetLabel = (fleet: Van | undefined | null) =>
  fleet?.id ? (
    <MenuItem key={fleet?.id} value={fleet?.id}>
      {fleet?.number}
    </MenuItem>
  ) : (
    <MenuItem key={0} value={0}>
      Unassigned fleet
    </MenuItem>
  );
